import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { OptimizationSuggestionState } from '@monitoring-panel/types';
import type { SuggestionDecision } from '@/services/api';
import { enqueueJobsAtom, isActive, queueJobsAtom, type QueueJob } from '@/store/optimizationQueue';

/**
 * The decision queue seen from one report: queue decisions on its
 * suggestions, and the queued or running job of each one, by key.
 */
export function useOptimizationQueue(executionId: string, reportDate: string) {
  const jobs = useAtomValue(queueJobsAtom);
  const enqueueJobs = useSetAtom(enqueueJobsAtom);

  const activeByKey = useMemo(() => {
    const map = new Map<string, QueueJob>();
    for (const job of jobs) {
      if (job.executionId === executionId && job.reportDate === reportDate && isActive(job)) {
        map.set(job.suggestionKey, job);
      }
    }
    return map;
  }, [jobs, executionId, reportDate]);

  const enqueue = useCallback(
    (suggestions: OptimizationSuggestionState[], decision: SuggestionDecision) => {
      enqueueJobs(
        suggestions.map((suggestion) => ({
          executionId,
          reportDate,
          suggestionKey: suggestion.key,
          decision,
          label: suggestion.entity_name ?? suggestion.text,
        }))
      );
    },
    [enqueueJobs, executionId, reportDate]
  );

  return { enqueue, activeByKey };
}
